import { Link } from "@remix-run/react";
import parse from "html-react-parser";

import { LEGAL, LEGAL_DETAILS_COMPLETE } from "~/config/legal";
import { useLanguage } from "~/context/LanguageContext";
import { useLocalizedPath } from "~/helpers/navigationHelper";
import common from "~/i18n/common";
import legal from "~/i18n/legal";
import { useT } from "~/i18n/useT";

import "./LegalPage.scss";

type Props = {
  title: string;
  sections: { h: string; body: string }[];
  /** The Imprint has no "last updated" line. */
  showUpdated?: boolean;
};

export default function LegalPage({
  title,
  sections,
  showUpdated = true,
}: Props) {
  const t = useT(legal);
  const c = useT(common);
  const { lang } = useLanguage();
  const localize = useLocalizedPath();

  const updated = new Intl.DateTimeFormat(lang, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date(LEGAL.lastUpdated));

  return (
    <main id="main-content">
      <section className="ss-container ss-container--prose ss-legal">
        <nav className="ss-crumbs" aria-label={c.a11y.breadcrumb}>
          <Link to={localize("/")}>{c.a11y.home}</Link>
          <span aria-hidden="true"> / </span>
          <span aria-current="page">{title}</span>
        </nav>

        <p className="ss-eyebrow ss-eyebrow--blue">{t.eyebrow}</p>
        <h1 className="ss-legal__title">{title}</h1>

        {showUpdated ? (
          <p className="ss-legal__updated">
            {t.lastUpdatedLabel} {updated}
          </p>
        ) : null}

        {!LEGAL_DETAILS_COMPLETE ? (
          // Loud on purpose. An Impressum missing its registration details is
          // an Abmahnung risk, and a silent placeholder would ship unnoticed.
          <p className="ss-legal__warning" role="alert">
            {t.incompleteWarning}
          </p>
        ) : null}

        <div className="ss-legal__sections">
          {sections.map((section) => (
            <section key={section.h} className="ss-card ss-legal__section">
              <h2 className="ss-legal__section-title">{section.h}</h2>
              <div className="ss-prose">{parse(section.body)}</div>
            </section>
          ))}
        </div>

        <div className="ss-legal__contact">
          <div>
            <h2 className="ss-legal__contact-title">{t.questionsTitle}</h2>
            <p className="ss-legal__contact-body">{t.questionsBody}</p>
          </div>
          <Link
            to={localize("/contact")}
            className="ss-btn ss-btn--primary ss-legal__contact-cta"
          >
            {c.cta.contactUs}
          </Link>
        </div>
      </section>
    </main>
  );
}
